'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Group } from 'three';
import { spaceCanvasXrStore } from '../../rendering/components/SpaceCanvas.js';
import { spaceCanvasXrBindings, useXrButtonBinding, useXrIndicator } from '../../rendering/components/XrBindings.js';
import { xrRuleText, xrTextPages, xrSubtitleLayout } from '../_pure/xrPresentation.js';
import { spokenWordFraction } from '../_pure/subtitleText.js';
/** Seconds of playback moved per second of full right-stick deflection while seeking. */
const XR_SEEK_RATE = 10;
const OWNER = 'Episode playback';
export function XrEntryButton({ onPause, ready, onEnter }) {
    const [supported, setSupported] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState();
    useEffect(() => {
        let mounted = true;
        let request = 0;
        const detect = () => {
            const generation = ++request;
            if (!navigator.xr)
                return;
            void navigator.xr.isSessionSupported('immersive-vr').then(value => {
                if (mounted && generation === request)
                    setSupported(value);
            }, (failure) => {
                console.error('[XR] Support detection failed:', failure);
                if (mounted && generation === request)
                    setError(String(failure));
            });
        };
        const system = navigator.xr;
        system?.addEventListener('devicechange', detect);
        const unsubscribe = spaceCanvasXrStore.subscribe((state, previous) => {
            if (state.emulator !== previous.emulator)
                detect();
        });
        detect();
        return () => {
            mounted = false;
            system?.removeEventListener('devicechange', detect);
            unsubscribe();
        };
    }, []);
    return _jsxs("div", { style: { position: 'absolute', right: 16, top: 16, zIndex: 20, color: 'white', background: '#102030', padding: 8 }, children: [_jsx("button", { disabled: !supported || !ready || pending, onClick: () => {
                    if (!supported || !ready || pending)
                        return;
                    onPause();
                    setPending(true);
                    setError(undefined);
                    void (onEnter ?? (() => spaceCanvasXrStore.enterVR()))().catch((failure) => {
                        console.error('[XR] Session entry failed:', failure);
                        setError(failure instanceof Error ? failure.message : String(failure));
                    }).finally(() => setPending(false));
                }, children: pending ? 'Entering XR...' : !supported ? 'XR unavailable' : !ready ? 'Loading XR view...' : 'Enter XR' }), error ? _jsx("div", { role: "alert", children: error }) : null] });
}
function XrButton({ label, x, y, width = 0.62, disabled = false, onClick }) {
    return _jsxs("group", { position: [x, y, 0], children: [_jsxs("mesh", { renderOrder: 10012, onClick: event => { event.stopPropagation(); if (!disabled)
                    onClick(); }, children: [_jsx("planeGeometry", { args: [width, 0.16] }), _jsx("meshBasicMaterial", { color: disabled ? '#28313a' : '#24506c', depthTest: false, depthWrite: false })] }), _jsxs(Text, { position: [0, 0, 0.005], fontSize: 0.052, maxWidth: width - 0.03, color: disabled ? '#889199' : 'white', anchorX: "center", anchorY: "middle", raycast: () => null, renderOrder: 10013, children: [label, _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] })] });
}
function XrText({ text, y, width = 2.4, fontSize = 0.055 }) {
    return _jsxs(Text, { position: [0, y, 0.01], fontSize: fontSize, maxWidth: width, lineHeight: 1.3, anchorX: "center", anchorY: "middle", textAlign: "center", color: "white", raycast: () => null, renderOrder: 10011, children: [text, _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] });
}
function XrReadingCard({ text, y, lines }) {
    const pages = useMemo(() => xrTextPages(text, 58, lines), [text, lines]);
    const [page, setPage] = useState(0);
    const index = Math.min(page, pages.length - 1);
    const height = lines * 0.073 + 0.16;
    return _jsxs("group", { position: [0, y, -2.6], renderOrder: 10010, children: [_jsxs("mesh", { renderOrder: 10010, children: [_jsx("planeGeometry", { args: [2.65, height] }), _jsx("meshBasicMaterial", { color: "#102030", transparent: true, opacity: 0.94, depthTest: false, depthWrite: false })] }), _jsx(XrText, { text: pages[index], y: 0 }), pages.length > 1 ? _jsx(XrButton, { label: `Text ${index + 1}/${pages.length} - next`, x: 0, y: -height / 2 - 0.1, width: 1, onClick: () => setPage((index + 1) % pages.length) }) : null] });
}
const SUBTITLE_FONT_SIZE = 0.06;
const SUBTITLE_ROW_HEIGHT = 0.085;
const SUBTITLE_SPACE = SUBTITLE_FONT_SIZE * 0.27;
function XrSubtitle({ text, trace, readProgress }) {
    const words = useMemo(() => xrSubtitleLayout(text), [text]);
    const [page, setPage] = useState(0);
    const materials = useRef([]);
    const underlines = useRef([]);
    const wordGroups = useRef([]);
    const widths = useRef([]);
    const lines = useRef(null);
    const progressPage = useRef(0);
    const pageCount = (words.at(-1)?.page ?? 0) + 1;
    const pageRows = words.reduce((rows, word) => word.page === page ? Math.max(rows, word.row + 1) : rows, 1);
    useFrame(() => {
        const progress = readProgress?.();
        const offset = progress?.text === text ? progress.offset : 0;
        const spoken = words.find(word => word.end >= offset) ?? words.at(-1);
        if (spoken && spoken.page !== progressPage.current) {
            progressPage.current = spoken.page;
            setPage(spoken.page);
        }
        // Words are separate meshes only so each can follow the voice; measured advances keep them reading as one line.
        const shown = words.flatMap((word, index) => word.page === page ? [index] : []);
        const measured = shown.every(index => widths.current[index] !== undefined);
        if (lines.current)
            lines.current.visible = measured;
        if (measured) {
            for (let row = 0; row < pageRows; row++) {
                const indices = shown.filter(index => words[index].row === row);
                const total = indices.reduce((sum, index) => sum + widths.current[index], 0)
                    + SUBTITLE_SPACE * Math.max(0, indices.length - 1);
                let x = -total / 2;
                for (const index of indices) {
                    const group = wordGroups.current[index];
                    if (group)
                        group.position.set(x, ((pageRows - 1) / 2 - row) * SUBTITLE_ROW_HEIGHT, 0.01);
                    x += widths.current[index] + SUBTITLE_SPACE;
                }
            }
        }
        words.forEach((word, index) => {
            const fraction = spokenWordFraction(word, offset);
            const material = materials.current[index];
            if (material)
                material.opacity = trace === 'brighten' ? 0.5 + fraction * 0.5 : 1;
            const underline = underlines.current[index];
            if (underline) {
                const width = (widths.current[index] ?? 0) * fraction;
                underline.scale.x = Math.max(width, 1e-4);
                underline.position.x = width / 2;
            }
        });
    });
    const height = pageRows * SUBTITLE_ROW_HEIGHT + 0.1;
    return _jsxs("group", { position: [0, -0.4, -2.6], children: [_jsxs("mesh", { renderOrder: 10010, children: [_jsx("planeGeometry", { args: [2.65, height] }), _jsx("meshBasicMaterial", { color: "#000000", transparent: true, opacity: 0.6, depthTest: false, depthWrite: false })] }), _jsx("group", { ref: lines, visible: false, children: words.map((word, index) => word.page === page ? _jsxs("group", { ref: group => { wordGroups.current[index] = group; }, children: [_jsxs(Text, { fontSize: SUBTITLE_FONT_SIZE, fontStyle: word.italic ? 'italic' : 'normal', anchorX: "left", anchorY: "middle", whiteSpace: "nowrap", renderOrder: 10011, raycast: () => null, onSync: (troika) => {
                                const bounds = troika.textRenderInfo?.blockBounds;
                                if (bounds)
                                    widths.current[index] = bounds[2] - bounds[0];
                            }, children: [word.text, _jsx("meshBasicMaterial", { ref: material => { materials.current[index] = material; }, color: "white", transparent: true, opacity: trace === 'brighten' ? 0.5 : 1, depthTest: false, depthWrite: false })] }), trace === 'underline' ? _jsxs("mesh", { ref: mesh => { underlines.current[index] = mesh; }, position: [0, -0.038, 0.001], renderOrder: 10011, scale: [1e-4, 1, 1], children: [_jsx("planeGeometry", { args: [1, 0.004] }), _jsx("meshBasicMaterial", { color: "white", depthTest: false, depthWrite: false })] }) : null] }, word.start) : null) }), pageCount > 1 ? _jsx(XrButton, { label: `Subtitle ${page + 1}/${pageCount} - next`, x: 0, y: -height / 2 - 0.1, width: 1, onClick: () => setPage((page + 1) % pageCount) }) : null] });
}
function useHeadLocked() {
    const group = useRef(null);
    useFrame(({ camera }) => {
        if (!group.current)
            return;
        camera.getWorldPosition(group.current.position);
        camera.getWorldQuaternion(group.current.quaternion);
    });
    return group;
}
function XrExitButton({ x, y, onExit }) {
    const session = useXR(state => state.session);
    return _jsx(XrButton, { label: "Exit XR", width: 0.5, x: x, y: y, onClick: () => {
            onExit?.();
            void session?.end().catch((error) => { console.error('[XR] Session exit failed:', error); });
        } });
}
const PICKER_OWNER = 'Episode picker';
/** Lets an XR viewer start an episode from exploration; hidden until B is pressed. */
export function XrEpisodePicker({ config }) {
    const session = useXR(state => state.session);
    const group = useHeadLocked();
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);
    const count = config.options.length;
    const choice = count ? config.options[Math.min(index, count - 1)] : undefined;
    useXrButtonBinding('a', { owner: PICKER_OWNER, legend: 'play / pause',
        onTap: () => spaceCanvasXrBindings.reject('right', 'No episode playing - press B to choose one') });
    useXrButtonBinding('b', { owner: PICKER_OWNER, legend: 'choose an episode', onTap: () => {
            if (count)
                setOpen(value => !value);
            else
                spaceCanvasXrBindings.reject('right', 'No episodes available here');
        } });
    if (!session || !open || !choice)
        return null;
    const position = Math.min(index, count - 1);
    return _jsx("group", { ref: group, renderOrder: 10010, children: _jsxs("group", { position: [0, -1.25, -2.6], children: [_jsxs("mesh", { position: [0, -0.08, -0.02], renderOrder: 10010, children: [_jsx("planeGeometry", { args: [2.65, 0.6] }), _jsx("meshBasicMaterial", { color: "#102030", depthTest: false, depthWrite: false })] }), _jsx(XrText, { text: "Choose an episode", y: 0.12, fontSize: 0.047 }), _jsx(XrButton, { label: "<", width: 0.18, x: -1.18, y: -0.1, onClick: () => setIndex((position + count - 1) % count) }), _jsx(XrButton, { label: `Load: ${choice.label}`, width: 1.6, x: -0.22, y: -0.1, onClick: () => {
                        setOpen(false);
                        config.onChoose(choice.value);
                    } }), _jsx(XrButton, { label: ">", width: 0.18, x: 0.76, y: -0.1, onClick: () => setIndex((position + 1) % count) }), _jsx(XrExitButton, { x: 1.1, y: -0.1 })] }) });
}
export function XrEpisodePlayer({ config, model, onIntent, textPanel, operationText, readSubtitleProgress }) {
    const session = useXR(state => state.session);
    const latestPause = useRef(config.onPause);
    useEffect(() => { latestPause.current = config.onPause; }, [config.onPause]);
    useEffect(() => {
        if (!session)
            return;
        latestPause.current();
        const visibility = () => { if (session.visibilityState !== 'visible')
            latestPause.current(); };
        const end = () => latestPause.current();
        session.addEventListener('visibilitychange', visibility);
        session.addEventListener('end', end);
        visibility();
        return () => {
            session.removeEventListener('visibilitychange', visibility);
            session.removeEventListener('end', end);
        };
    }, [session]);
    const group = useHeadLocked();
    const section = model.sections.find(section => section.sectionId === config.episodeSectionId);
    const selection = section?.sectionKind === 'select' ? section : undefined;
    const options = selection ? selection.options.filter(option => option.value) : [];
    const [candidate, setCandidate] = useState();
    const selected = selection?.sectionKind === 'select' ? selection.selectedValue : '';
    const candidateIndex = Math.max(0, options.findIndex(option => option.value === (candidate ?? selected)));
    const choice = options[candidateIndex];
    const actions = model.sections.flatMap(section => section.sectionKind === 'actions' ? section.actions : []);
    const toggle = actions.find(action => action.playbackRole === 'toggle');
    const reset = actions.find(action => action.playbackRole === 'reset');
    const sound = model.sections.find(section => section.sectionKind === 'checkbox' && section.sectionId === config.soundSectionId);
    const error = config.errorSectionId ? model.sections.find(section => section.sectionId === config.errorSectionId) : undefined;
    const [panelOpen, setPanelOpen] = useState(false);
    useXrButtonBinding('a', {
        owner: OWNER, legend: 'play / pause',
        onTap: () => {
            if (toggle && toggle.enabled !== false)
                onIntent({ overlayIntentKind: 'action-triggered', actionId: toggle.actionId });
            else
                spaceCanvasXrBindings.reject('right', 'Playback unavailable');
        },
        hold: {
            legend: 'seek with right stick',
            onStart: () => { if (!config.seek)
                spaceCanvasXrBindings.notify('Seek is not available for this episode'); },
            onStickX: (x, delta) => { if (x !== 0)
                config.seek?.(x * XR_SEEK_RATE * delta); },
        },
    });
    useXrButtonBinding('b', { owner: OWNER, legend: 'show / hide episode controls', onTap: () => setPanelOpen(open => !open) });
    useXrIndicator('right', OWNER, toggle ? toggle.isActive
        ? { color: '#3ccf6e', label: 'Playing' } : { color: '#f0b030', label: 'Paused' } : undefined);
    if (!session)
        return null;
    return _jsxs("group", { ref: group, renderOrder: 10010, children: [error?.sectionKind === 'actions' && error.description ? _jsx(XrReadingCard, { text: `${error.label}\n${error.description}`, y: 1.5, lines: 3 }, error.description) : null, textPanel ? _jsx(XrReadingCard, { text: xrRuleText(textPanel), y: 0.38, lines: 13 }, xrRuleText(textPanel)) : null, operationText ? _jsx(XrReadingCard, { text: operationText, y: textPanel ? 1.12 : 0.68, lines: 2 }, "operation") : null, model.sceneSubtitle ? _jsx(XrSubtitle, { text: model.sceneSubtitle, ...(model.sceneSubtitleTrace ? { trace: model.sceneSubtitleTrace } : {}), ...(readSubtitleProgress ? { readProgress: readSubtitleProgress } : {}) }, model.sceneSubtitle) : null, panelOpen ? _jsxs("group", { position: [0, -1.25, -2.6], children: [_jsxs("mesh", { position: [0, -0.08, -0.02], renderOrder: 10010, children: [_jsx("planeGeometry", { args: [2.65, 0.78] }), _jsx("meshBasicMaterial", { color: "#102030", depthTest: false, depthWrite: false })] }), _jsx(XrText, { text: options.find(option => option.value === selected)?.label ?? model.title, y: 0.2, fontSize: 0.047 }), toggle ? _jsx(XrButton, { label: toggle.label, x: -0.93, y: 0, disabled: toggle.enabled === false, onClick: () => onIntent({ overlayIntentKind: 'action-triggered', actionId: toggle.actionId }) }) : null, reset ? _jsx(XrButton, { label: reset.label, x: -0.26, y: 0, disabled: reset.enabled === false, onClick: () => onIntent({ overlayIntentKind: 'action-triggered', actionId: reset.actionId }) }) : null, sound?.sectionKind === 'checkbox' ? _jsx(XrButton, { label: sound.checked ? 'Sound on' : 'Sound off', x: 0.41, y: 0, disabled: sound.enabled === false, onClick: () => onIntent({
                            overlayIntentKind: 'checkbox-changed', sectionId: sound.sectionId, checked: !sound.checked,
                        }) }) : null, _jsx(XrExitButton, { x: 1.04, y: 0, onExit: config.onPause }), choice ? _jsxs(_Fragment, { children: [_jsx(XrButton, { label: "<", width: 0.18, x: -1.18, y: -0.23, onClick: () => setCandidate(options[(candidateIndex + options.length - 1) % options.length].value) }), _jsx(XrButton, { label: choice.value === selected ? choice.label : `Load: ${choice.label}`, width: 2.1, x: 0, y: -0.23, disabled: selection?.enabled === false || choice.value === selected, onClick: () => onIntent({ overlayIntentKind: 'select-changed', sectionId: config.episodeSectionId, value: choice.value }) }), _jsx(XrButton, { label: ">", width: 0.18, x: 1.18, y: -0.23, onClick: () => setCandidate(options[(candidateIndex + 1) % options.length].value) })] }) : null, _jsx(XrText, { text: selection?.enabled === false ? 'Reset playback to load another episode'
                            : toggle?.isActive ? 'Playing' : 'Ready / paused', y: -0.39, fontSize: 0.035 })] }) : null] });
}
